import type { ErrorEvent } from '@sentry/nextjs'

/**
 * Stable fingerprint for Google Maps `EpochMismatch` errors.
 *
 * The error message embeds the two mismatched epoch numbers (e.g.
 * `EpochMismatch,790,789`), so Sentry's default grouping mints a brand new
 * issue every time Google rolls a map data epoch. Forcing one fingerprint
 * collapses them into a single group that can be archived until it escalates.
 */
export const GOOGLE_MAPS_EPOCH_MISMATCH_FINGERPRINT = 'google-maps-epoch-mismatch'

const EPOCH_MISMATCH_PATTERN = /EpochMismatch/

/**
 * Detects the internal assertion thrown by the Google Maps vector/WebGL
 * renderer when Google rolls a new map data epoch server-side while a
 * long-lived tab is still running the previous one.
 *
 * It is raised inside `webgl.js` with no first-party frames, is already handled
 * by the Maps SDK, and self-heals on reload — so it is not actionable for us.
 * We're pinned to the `beta` channel (required by `google.maps.routes`, used
 * for ETA), which rolls more often than `weekly`, so expect it regularly.
 *
 * Matches every exception in the chain, since the SDK re-throws the original
 * error wrapped in Google's internal error-code envelope.
 */
export const isGoogleMapsEpochMismatch = (event: ErrorEvent): boolean =>
  event.exception?.values?.some((value) => EPOCH_MISMATCH_PATTERN.test(value.value ?? '')) ?? false

const MODULE_LOAD_FAILURE_PATTERN = /Could not load "\w+"/
const GOOGLE_MAPS_SCRIPT_PATTERN = /maps-api-v3\//

/**
 * Detects the Maps SDK failing to fetch one of its own lazily loaded modules
 * (e.g. `Could not load "webgl".`), typically because a mobile connection
 * dropped or iOS Safari suspended the tab mid-download.
 *
 * Google's internal loader starts the download and never handles the
 * rejection, so we can't catch or retry it, and a reload fixes it. Like the
 * epoch mismatch, it's `beta`-channel renderer noise, so it shares that group.
 *
 * Only matches when a stack frame comes from Google's `maps-api-v3` script, so
 * a first-party error with a similar message is never swallowed.
 */
export const isGoogleMapsModuleLoadFailure = (event: ErrorEvent): boolean =>
  event.exception?.values?.some(
    (value) =>
      MODULE_LOAD_FAILURE_PATTERN.test(value.value ?? '') &&
      (value.stacktrace?.frames?.some((frame) =>
        GOOGLE_MAPS_SCRIPT_PATTERN.test(frame.filename ?? ''),
      ) ??
        false),
  ) ?? false
